import { Button, Tag } from '@navi/web-ui/lib/primitives';
import Typography from '@navi/web-ui/lib/primitives/Typography/index';
import CustomLogoutIcon from '@cp/src/assets/icons/CustomLogoutIcon';
import CustomUserIcon from '@cp/src/assets/icons/CustomUserIcon';
import EscalationUserIcon from '@cp/src/assets/icons/EscalationUserIcon';
import UserDocuments from '../UserDocuments';
import styles from './index.module.scss';
import { useSelector } from 'react-redux';
import { RootState } from '@cp/src/store';
import { useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import APP_ROUTES from '@cp/src/layout/Routes';
import { CLICK_CONFIG } from '../constants/constants';
import { Roles } from '@cp/src/pages/auth/constants/AuthConstants';

interface LogoutModalProps {
  showEscalationCount?: boolean;
  agentEscalationCount?: number;
  showHorizontalLine?: boolean;
  disableLogout?: boolean;
  logOutHandler: () => void;
  isVisible?: boolean;
}

const LogoutModal = ({
  showEscalationCount,
  agentEscalationCount,
  showHorizontalLine,
  disableLogout,
  logOutHandler,
  isVisible
}: LogoutModalProps) => {
  const user = useSelector((state: RootState) => state?.common?.userData);
  const [clicks, setClicks] = useState(0);
  const lastClickTime = useRef(0);
  const navigate = useNavigate();

  const roles = useSelector((state: RootState) => state?.common?.userData?.roles);
  const isImpersonationAllowed = roles?.includes(Roles.ROLE_DEBUG_READ);

  const handleUserIconClick = () => {
    if (!isImpersonationAllowed) return;
    const now = Date.now();
    const isWithinTimeWindow = now - lastClickTime.current <= CLICK_CONFIG.TIME_WINDOW;

    if (!isWithinTimeWindow) {
      setClicks(0);
    } else if (clicks >= CLICK_CONFIG.REQUIRED_CLICKS) {
      navigate(APP_ROUTES.IMPERSONATION.path);
      setClicks(0);
      return;
    }

    lastClickTime.current = now;
    setClicks(prev => prev + 1);
  };

  if (!isVisible) {
    return null;
  }

  return (
    <div className={styles.logoutContainer}>
      <div className="flex flex-col gap-y-4 w-full">
        <div className={styles.agentDetails}>
          <div
            className="flex items-center justify-center w-10 h-10 rounded-full bg-gray-100"
            onClick={handleUserIconClick}
          >
            {showEscalationCount ? (
              <EscalationUserIcon width={40} height={40} fillColor="var(--greyscale-light)" />
            ) : (
              <CustomUserIcon width={40} height={40} fillColor="var(--greyscale-light)" />
            )}
          </div>

          <div className="flex flex-col">
            <div className={styles.agentNameContainer}>
              <Typography
                variant={'h6'}
                className="max-w-[138px] text-ellipsis whitespace-nowrap overflow-hidden"
              >
                {user?.name}
              </Typography>
              <Typography
                variant={'p5'}
                className="max-w-[138px] text-ellipsis whitespace-nowrap overflow-hidden mt-1"
              >
                {user?.email}
              </Typography>
            </div>
            {showEscalationCount ? (
              <>
                <div className={'mt-2'}>
                  <Tag
                    color="red"
                    label={`${agentEscalationCount} escalation in last 60 days`}
                    size="sm"
                    variant="transparent"
                  />
                </div>
              </>
            ) : null}
            {/* To be removed later */}
          </div>
        </div>
        {showHorizontalLine ? <div className={styles.horizontalLine} /> : null}
        <UserDocuments />
      </div>

      <Button
        variant="secondary"
        onClick={logOutHandler}
        className={'w-full h-[40px]'}
        disabled={disableLogout}
      >
        <div className="flex flex-row items-center gap-x-2">
          <Typography variant="h6" className={styles.logoutText}>
            Logout
          </Typography>
          <CustomLogoutIcon width={16} height={16} fillColor="var(--grayscale-2)" />
        </div>
      </Button>
    </div>
  );
};

export default LogoutModal;
